import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Music, TrendingUp, Clock, Zap, Award, ArrowLeft } from "lucide-react";
import { ApexAreaChart } from "@/components/ui/premium-charts/ApexAreaChart";
import { ApexRadialChart } from "@/components/ui/premium-charts/ApexRadialChart";
import { Skeleton } from "@/components/ui/skeleton";
import { SmartRecommendations } from "@/components/music/SmartRecommendations";
import { useNavigate } from "react-router-dom";
import { useBPM } from "@/hooks/useBPM";
import { Button } from "@/components/ui/button";
import { useUserAge } from "@/hooks/useUserAge";

const MusicAnalytics = () => {
  const navigate = useNavigate();
  const userAge = useUserAge();
  const bpm = useBPM(userAge);

  const { data: stats, isLoading } = useQuery({
    queryKey: ['music-analytics'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data: history } = await supabase
        .from('run_music_history')
        .select('*')
        .order('played_at', { ascending: false });

      if (!history || history.length === 0) return null;

      // Calculate statistics
      const totalTracks = history.length;
      const avgUserBPM = Math.round(
        history.reduce((sum, h) => sum + h.user_bpm, 0) / totalTracks
      );

      const syncQualities = {
        perfect: history.filter(h => h.sync_quality === 'perfect').length,
        good: history.filter(h => h.sync_quality === 'good').length,
        adjusting: history.filter(h => h.sync_quality === 'adjusting').length,
        poor: history.filter(h => h.sync_quality === 'poor').length,
      };

      const syncRate = Math.round(
        ((syncQualities.perfect + syncQualities.good) / totalTracks) * 100
      );

      // Genre analysis
      const genreCounts = history.reduce((acc, h) => {
        if (h.genre) {
          acc[h.genre] = (acc[h.genre] || 0) + 1;
        }
        return acc;
      }, {} as Record<string, number>);

      const topGenres = Object.entries(genreCounts)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 5)
        .map(([genre, count]) => ({ genre, count }));

      // Energy analysis
      const avgEnergy = history.filter(h => h.energy).length > 0
        ? Math.round(
            history.reduce((sum, h) => sum + (h.energy || 0), 0) / 
            history.filter(h => h.energy).length
          )
        : null;

      // Most played tracks
      const trackCounts = history.reduce((acc, h) => {
        const key = `${h.track_name}|||${h.artist}`;
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      const topTracks = Object.entries(trackCounts)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 10)
        .map(([key, count]) => {
          const [track_name, artist] = key.split('|||');
          return { track_name, artist, count };
        });

      // BPM over time
      const bpmOverTime = history
        .slice(0, 50)
        .reverse()
        .map(h => h.user_bpm);
      
      const bpmCategories = history
        .slice(0, 50)
        .reverse()
        .map((_, idx) => `${idx + 1}`);

      return {
        totalTracks,
        avgUserBPM,
        syncRate,
        syncQualities,
        topGenres,
        avgEnergy,
        topTracks,
        bpmOverTime,
        bpmCategories,
      };
    },
  });

  if (isLoading) {
    return (
      <div className="container max-w-6xl mx-auto p-6 space-y-6">
        <Skeleton className="h-12 w-64" />
        <div className="grid gap-6 md:grid-cols-3">
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="container max-w-6xl mx-auto p-6">
        <Card className="p-12 text-center">
          <Music className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
          <h2 className="text-2xl font-bold mb-2">Nenhum dado musical ainda</h2>
          <p className="text-muted-foreground">
            Complete uma corrida com música para ver suas análises aqui
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="container max-w-6xl mx-auto p-6 space-y-6">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold mb-2">Analytics Musical</h1>
          <p className="text-muted-foreground">
            Insights sobre como a música impacta suas corridas
          </p>
        </div>
      </div>

      {/* Smart Recommendations Section */}
      <SmartRecommendations
        currentBPM={bpm.currentBPM}
        mood={getMoodFromBPM(bpm.currentBPM)}
        timeOfDay={getTimeOfDay()}
      />

      {/* Summary Cards */}
      <div className="grid gap-6 md:grid-cols-3">
        <Card className="p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-primary/10">
              <Music className="h-6 w-6 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total de Músicas</p>
              <p className="text-2xl font-bold">{stats.totalTracks}</p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-green-500/10">
              <Zap className="h-6 w-6 text-green-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Taxa de Sincronia</p>
              <p className="text-2xl font-bold">{stats.syncRate}%</p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-blue-500/10">
              <TrendingUp className="h-6 w-6 text-blue-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">BPM Médio</p>
              <p className="text-2xl font-bold">{stats.avgUserBPM}</p>
            </div>
          </div>
        </Card>
      </div>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList>
          <TabsTrigger value="overview">Visão Geral</TabsTrigger>
          <TabsTrigger value="genres">Gêneros</TabsTrigger>
          <TabsTrigger value="tracks">Top Músicas</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          {/* BPM Over Time Chart */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Evolução do BPM</h3>
            <ApexAreaChart
              series={[{
                name: 'BPM',
                data: stats.bpmOverTime
              }]}
              categories={stats.bpmCategories}
              height={300}
            />
          </Card>

          {/* Sync Quality Distribution */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Qualidade de Sincronização</h3>
            <ApexRadialChart
              series={[
                Math.round((stats.syncQualities.perfect / stats.totalTracks) * 100),
                Math.round((stats.syncQualities.good / stats.totalTracks) * 100),
                Math.round((stats.syncQualities.adjusting / stats.totalTracks) * 100),
              ]}
              labels={['Perfeita', 'Boa', 'Ajustando']}
              height={300}
            />
          </Card>

          {/* Energy Level */}
          {stats.avgEnergy && (
            <Card className="p-6">
              <div className="flex items-center gap-4">
                <Award className="h-8 w-8 text-primary" />
                <div>
                  <p className="text-sm text-muted-foreground">Energia Média das Músicas</p>
                  <p className="text-3xl font-bold">{stats.avgEnergy}%</p>
                </div>
              </div>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="genres" className="space-y-6">
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Top 5 Gêneros</h3>
            <div className="space-y-4">
              {stats.topGenres.map((genre, idx) => (
                <div key={idx} className="flex items-center gap-4">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold">
                    {idx + 1}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium">{genre.genre}</p>
                    <div className="h-2 bg-muted rounded-full overflow-hidden mt-1">
                      <div
                        className="h-full bg-primary"
                        style={{
                          width: `${(genre.count / stats.totalTracks) * 100}%`
                        }}
                      />
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground">{genre.count} músicas</p>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="tracks" className="space-y-6">
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Top 10 Músicas</h3>
            <div className="space-y-3">
              {stats.topTracks.map((track, idx) => (
                <div key={idx} className="flex items-center gap-4 p-3 rounded-lg bg-muted/50">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold">
                    {idx + 1}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium">{track.track_name}</p>
                    <p className="text-sm text-muted-foreground">{track.artist}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium">{track.count}x</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

function getMoodFromBPM(bpm: number): string {
  if (bpm < 100) return 'calm';
  if (bpm < 140) return 'focused';
  if (bpm < 160) return 'energized';
  return 'motivated';
}

function getTimeOfDay(): string {
  const hour = new Date().getHours();
  if (hour < 6) return 'night';
  if (hour < 12) return 'morning';
  if (hour < 18) return 'afternoon';
  return 'evening';
}

export default MusicAnalytics;
